import React, { Component } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import { GetUserAction, UpdateUserAction } from "../../../Actions/Authentication";
import axios from "../../../Actions/Axios";
import SettingsShell from "./SettingsShell";
import AccountSettingsPage from "./AccountSettingsPage";
import AdminSettingsPage from "./AdminSettingsPage";
import EmailServiceSettingsPage from "./EmailServiceSettingsPage";

const SETTINGS_TABS = [
  {
    key: "account",
    label: "Account Settings",
    path: "/dashboard/settings/account",
  },
  {
    key: "admin",
    label: "Admin Settings",
    path: "/dashboard/settings/admin",
  },
  {
    key: "email",
    label: "Email Service",
    path: "/dashboard/settings/email-service",
    requiresPrimeSuperuser: true,
  },
];

class Settings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editingField: null,
      editingValue: "",
      saving: false,
      fieldError: null,
      emailConfig: {
        provider: "sendgrid",
        sender_name: "",
        sender_local_part: "",
        sender_domain: "",
        api_key: "",
        credentials: {
          host: "",
          port: 587,
          username: "",
          password: "",
          use_tls: true,
          use_ssl: false,
        },
        is_active: true,
      },
      loadingEmailConfig: false,
      savingEmailConfig: false,
      emailConfigError: null,
      emailConfigSuccess: null,
      whatsAppConfig: {
        welcome_message: "",
        is_welcome_enabled: true,
      },
      loadingWhatsAppConfig: false,
      savingWhatsAppConfig: false,
      whatsAppConfigError: null,
      whatsAppConfigSuccess: null,
    };
    this.editRowRef = React.createRef();
  }

  static propTypes = {
    navigate: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    user: PropTypes.object,
    GetUserAction: PropTypes.func.isRequired,
    UpdateUserAction: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.props.GetUserAction();
    document.addEventListener("mousedown", this.handleClickOutside);
    this.ensureValidSettingsRoute();
  }

  componentDidUpdate(prevProps) {
    const becamePrime =
      !prevProps.user?.is_prime_superuser && this.props.user?.is_prime_superuser;
    if (becamePrime && this.getActiveTabKey() === "email") {
      this.fetchEmailServiceConfig();
    }

    const becameAdmin =
      (!prevProps.user?.is_staff && this.props.user?.is_staff) ||
      (!prevProps.user?.is_superuser && this.props.user?.is_superuser);
    if (becameAdmin && this.getActiveTabKey() === "admin") {
      this.fetchWhatsAppConfig();
    }

    if (
      prevProps.location.pathname !== this.props.location.pathname ||
      prevProps.user?.is_prime_superuser !== this.props.user?.is_prime_superuser
    ) {
      this.ensureValidSettingsRoute();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  getAvailableTabs = () =>
    SETTINGS_TABS.filter(
      (tab) => !tab.requiresPrimeSuperuser || this.props.user?.is_prime_superuser
    );

  getActiveTabKey = () => {
    const { pathname } = this.props.location;
    if (pathname.includes("/dashboard/settings/admin")) return "admin";
    if (pathname.includes("/dashboard/settings/email-service")) return "email";
    return "account";
  };

  ensureValidSettingsRoute = () => {
    const { pathname } = this.props.location;
    const activeTab = this.getActiveTabKey();
    const availableTabs = this.getAvailableTabs();
    const activeTabConfig = availableTabs.find((tab) => tab.key === activeTab);

    if (pathname === "/dashboard/settings" || !activeTabConfig) {
      this.props.navigate(availableTabs[0].path, { replace: true });
      return;
    }

    if (activeTab === "email" && this.props.user?.is_prime_superuser) {
      this.fetchEmailServiceConfig();
    }
    if (activeTab === "admin" && (this.props.user?.is_staff || this.props.user?.is_superuser)) {
      this.fetchWhatsAppConfig();
    }
  };

  handleClickOutside = (e) => {
    if (
      this.state.editingField !== null &&
      this.editRowRef.current &&
      !this.editRowRef.current.contains(e.target)
    ) {
      this.cancelEdit();
    }
  };

  handleNavigateToAdmin = () => {
    this.props.navigate("/admin-dashboard");
  };

  handleTabChange = (tabKey) => {
    const tab = this.getAvailableTabs().find((item) => item.key === tabKey);
    if (!tab) return;
    this.props.navigate(tab.path);
  };

  fetchEmailServiceConfig = async () => {
    if (!this.props.user?.is_prime_superuser) return;

    this.setState({
      loadingEmailConfig: true,
      emailConfigError: null,
      emailConfigSuccess: null,
    });
    try {
      const res = await axios.get("/api/messaging/email-service-config/");
      const cfg = res?.data || {};
      this.setState((prev) => ({
        emailConfig: {
          ...prev.emailConfig,
          provider: cfg.provider || prev.emailConfig.provider,
          sender_name: cfg.sender_name || "",
          sender_local_part: cfg.sender_local_part || "",
          sender_domain: cfg.sender_domain || "",
          is_active:
            typeof cfg.is_active === "boolean"
              ? cfg.is_active
              : prev.emailConfig.is_active,
        },
      }));
    } catch (err) {
      if (err?.response?.status !== 404) {
        const msg = err?.response?.data?.detail || "Failed to load email config.";
        this.setState({ emailConfigError: msg });
      }
    } finally {
      this.setState({ loadingEmailConfig: false });
    }
  };

  onEmailConfigChange = (field, value) => {
    this.setState((prev) => ({
      emailConfig: {
        ...prev.emailConfig,
        [field]: value,
      },
      emailConfigError: null,
      emailConfigSuccess: null,
    }));
  };

  onEmailCredentialChange = (field, value) => {
    this.setState((prev) => ({
      emailConfig: {
        ...prev.emailConfig,
        credentials: {
          ...prev.emailConfig.credentials,
          [field]: value,
        },
      },
      emailConfigError: null,
      emailConfigSuccess: null,
    }));
  };

  saveEmailServiceConfig = async () => {
    const { emailConfig } = this.state;
    this.setState({
      savingEmailConfig: true,
      emailConfigError: null,
      emailConfigSuccess: null,
    });

    const payload = {
      provider: emailConfig.provider,
      sender_name: emailConfig.sender_name,
      sender_local_part: emailConfig.sender_local_part,
      sender_domain: emailConfig.sender_domain,
      is_active: emailConfig.is_active,
    };

    if (emailConfig.provider === "sendgrid") {
      if (emailConfig.api_key) payload.api_key = emailConfig.api_key;
    } else if (emailConfig.provider === "smtp") {
      payload.credentials = emailConfig.credentials;
    }

    try {
      await axios.post("/api/messaging/email-service-config/", payload);
      this.setState({
        emailConfigSuccess: "Email service configuration saved.",
        emailConfig: {
          ...emailConfig,
          api_key: "",
          credentials: {
            ...emailConfig.credentials,
            password: "",
          },
        },
      });
      await this.fetchEmailServiceConfig();
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        data?.detail ||
        data?.api_key?.[0] ||
        data?.credentials ||
        "Failed to save email config.";
      this.setState({ emailConfigError: msg });
    } finally {
      this.setState({ savingEmailConfig: false });
    }
  };

  fetchWhatsAppConfig = async () => {
    this.setState({ loadingWhatsAppConfig: true, whatsAppConfigError: null });
    try {
      const res = await axios.get("/api/messaging/whatsapp-config/");
      this.setState({
        whatsAppConfig: {
          welcome_message: res.data.welcome_message || "",
          is_welcome_enabled: res.data.is_welcome_enabled !== false,
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load WhatsApp config.";
      this.setState({ whatsAppConfigError: msg });
    } finally {
      this.setState({ loadingWhatsAppConfig: false });
    }
  };

  saveWhatsAppConfig = async () => {
    const { whatsAppConfig } = this.state;
    this.setState({ savingWhatsAppConfig: true, whatsAppConfigError: null, whatsAppConfigSuccess: null });
    try {
      await axios.put("/api/messaging/whatsapp-config/", whatsAppConfig);
      this.setState({ whatsAppConfigSuccess: "WhatsApp configuration saved." });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save WhatsApp config.";
      this.setState({ whatsAppConfigError: msg });
    } finally {
      this.setState({ savingWhatsAppConfig: false });
    }
  };

  onWhatsAppConfigChange = (field, value) => {
    this.setState((prev) => ({
      whatsAppConfig: { ...prev.whatsAppConfig, [field]: value },
      whatsAppConfigError: null,
      whatsAppConfigSuccess: null,
    }));
  };

  startEdit = (fieldKey, currentValue) => {
    this.setState({ editingField: fieldKey, editingValue: currentValue || "" });
  };

  cancelEdit = () => {
    this.setState({ editingField: null, editingValue: "", fieldError: null });
  };

  saveEdit = async (fieldKey) => {
    this.setState({ saving: true, fieldError: null });
    try {
      await this.props.UpdateUserAction({ [fieldKey]: this.state.editingValue });
      this.setState({ editingField: null, editingValue: "" });
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.[fieldKey] || errData?.detail || "Failed to save.";
      this.setState({ fieldError: msg });
    } finally {
      this.setState({ saving: false });
    }
  };

  renderActivePage = () => {
    const activeTab = this.getActiveTabKey();
    const { user } = this.props;
    const {
      editingField,
      editingValue,
      saving,
      fieldError,
      emailConfig,
      loadingEmailConfig,
      savingEmailConfig,
      emailConfigError,
      emailConfigSuccess,
      whatsAppConfig,
      loadingWhatsAppConfig,
      savingWhatsAppConfig,
      whatsAppConfigError,
      whatsAppConfigSuccess,
    } = this.state;

    if (activeTab === "admin") {
      return (
        <AdminSettingsPage
          user={user}
          onNavigateToAdmin={this.handleNavigateToAdmin}
          whatsAppConfig={whatsAppConfig}
          loadingWhatsAppConfig={loadingWhatsAppConfig}
          savingWhatsAppConfig={savingWhatsAppConfig}
          whatsAppConfigError={whatsAppConfigError}
          whatsAppConfigSuccess={whatsAppConfigSuccess}
          onWhatsAppConfigChange={this.onWhatsAppConfigChange}
          onSaveWhatsAppConfig={this.saveWhatsAppConfig}
        />
      );
    }

    if (activeTab === "email") {
      return (
        <EmailServiceSettingsPage
          user={user}
          emailConfig={emailConfig}
          loadingEmailConfig={loadingEmailConfig}
          savingEmailConfig={savingEmailConfig}
          emailConfigError={emailConfigError}
          emailConfigSuccess={emailConfigSuccess}
          onEmailConfigChange={this.onEmailConfigChange}
          onEmailCredentialChange={this.onEmailCredentialChange}
          onSaveEmailServiceConfig={this.saveEmailServiceConfig}
        />
      );
    }

    return (
      <AccountSettingsPage
        user={user}
        editingField={editingField}
        editingValue={editingValue}
        saving={saving}
        fieldError={fieldError}
        editRowRef={this.editRowRef}
        onStartEdit={this.startEdit}
        onCancelEdit={this.cancelEdit}
        onSaveEdit={this.saveEdit}
        onEditingValueChange={(value) =>
          this.setState({ editingValue: value, fieldError: null })
        }
      />
    );
  };

  render() {
    const activeTab = this.getActiveTabKey();

    return (
      <SettingsShell
        user={this.props.user}
        tabs={this.getAvailableTabs()}
        activeTab={activeTab}
        onTabChange={this.handleTabChange}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          >
            {this.renderActivePage()}
          </motion.div>
        </AnimatePresence>
      </SettingsShell>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.authentication.user,
});

export default withRouterHooks(
  connect(mapStateToProps, { GetUserAction, UpdateUserAction })(Settings)
);
